<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light'
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">

          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <b-link
              class="navbar-brand"
              to="/"
            >
              <futuro-logo />
              <futuro-text class="ml-1" v-if="!isVerticalMenuCollapsed || isMouseHovered"/>

            </b-link>
          </li>

          <!-- Toggler Button -->
          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div
      :class="{'d-block': shallShadowBottom}"
      class="shadow-bottom"
    />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area oraculo-menu"
      tagname="ul"
      @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
    >
      <vertical-nav-menu-items
        :items="navMenuItems"
        class="navigation navigation-main"
      />
    </vue-perfect-scrollbar>
    <!-- /main menu content-->
  </div>
</template>

<script>
import FuturoLogo from '@core/layouts/components/Logo.vue'
import FuturoText from '@core/layouts/components/LogoText.vue'
import navMenuItems from '@/navigation'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { BLink, BImg } from 'bootstrap-vue'
import { provide, computed, ref } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import { $themeConfig } from '@themeConfig'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import useVerticalNavMenu from './useVerticalNavMenu'

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
    FuturoLogo,
    FuturoText
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    provide('isMouseHovered', isMouseHovered)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'))

    // App Name
    const { appName, appLogoImage } = $themeConfig.app

    return {
      navMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,
    }
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";
.oraculo-menu {
  padding-bottom: 90px !important;
}

// -----------------------------------------------------------------------------------------------------------------
// Trava de report pendente (board v4-pendencias; ver mixinReportLockedNavItem.js e vertical-nav-menu-header):
// itens esmaecidos, com cadeado e sem navegação — exceto "Compromissos" — e o aviso sob o cabeçalho MENU.
// Sem a trava, nenhuma destas classes aparece no menu.
// -----------------------------------------------------------------------------------------------------------------
$report-lock-ease: cubic-bezier(.2, 0, 0, 1);

.main-menu .navigation {
  // Só o estado travado ganha transição: o menu destravado (hover do Vuexy incluído) fica exatamente como era.
  li.nav-item.nav-item--report-locked > a {
    cursor: not-allowed;

    > .menu-title,
    > .feather:not(.nav-report-lock-icon) {
      opacity: .72;
      transition: opacity 240ms $report-lock-ease;
    }

    // Sem o deslocamento de hover do Vuexy: o item não navega.
    &:hover > * {
      transform: none;
    }
  }

  // Grupo travado: a seta do Vuexy (::after, que já tem transição) dá lugar ao cadeado.
  li.nav-item.has-sub.nav-item--report-locked > a:after {
    opacity: 0;
  }

  // Opacidade própria (não só a do estado travado): ao liberar, o cadeado esmaece a partir do mesmo tom.
  li.nav-item > a > .nav-report-lock-icon {
    flex-shrink: 0;
    width: 14px !important;
    height: 14px !important;
    margin: 0 0 0 auto !important;
    font-size: 14px !important;
    opacity: .72;
  }

  li.nav-item > a > .nav-report-lock-icon--group {
    margin-right: 5px !important;
  }

  .nav-report-lock-enter-active,
  .nav-report-lock-leave-active {
    transition: opacity 200ms $report-lock-ease;
  }

  li.nav-item > a > .nav-report-lock-enter,
  li.nav-item > a > .nav-report-lock-leave-to {
    opacity: 0;
  }

  // "Disponível após finalizar os reports pendentes" — 12/16 500, cadeado de 12px.
  .navigation-header .nav-report-lock-caption {
    display: grid;
    grid-template-rows: 1fr;
    margin-top: 8px;
    padding-right: 1rem;
    color: #6e6b7b;
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    letter-spacing: normal;
    text-transform: none;
    white-space: normal;
  }

  .nav-report-lock-caption__inner {
    min-height: 0;
    overflow: hidden;
    display: flex;
    align-items: flex-start;
    gap: 6px;
  }

  .nav-report-lock-caption__icon {
    flex-shrink: 0;
    margin-top: 2px;
  }

  .nav-report-lock-caption-enter-active,
  .nav-report-lock-caption-leave-active {
    transition:
      grid-template-rows 240ms cubic-bezier(.16, 1, .3, 1),
      margin-top 240ms cubic-bezier(.16, 1, .3, 1),
      opacity 200ms $report-lock-ease;
  }

  .navigation-header .nav-report-lock-caption-enter,
  .navigation-header .nav-report-lock-caption-leave-to {
    grid-template-rows: 0fr;
    margin-top: 0;
    opacity: 0;
  }
}

body.dark-layout .main-menu .navigation .navigation-header .nav-report-lock-caption,
.main-menu.menu-dark .navigation .navigation-header .nav-report-lock-caption {
  color: #a0a6b8;
}

// Menu recolhido (sem hover): só os ícones aparecem — o cadeado e o aviso também saem.
.main-menu:not(.expanded) .navigation {
  .nav-report-lock-icon,
  .nav-report-lock-caption {
    display: none !important;
  }
}

@media (prefers-reduced-motion: reduce) {
  .main-menu .navigation {
    li.nav-item.nav-item--report-locked > a > .menu-title,
    li.nav-item.nav-item--report-locked > a > .feather:not(.nav-report-lock-icon),
    .nav-report-lock-enter-active,
    .nav-report-lock-leave-active,
    .nav-report-lock-caption-enter-active,
    .nav-report-lock-caption-leave-active {
      transition: none;
    }
  }
}
</style>
